import { customAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

const PASSWORD_PLACEHOLDER = "__TINES_POSTGRES_PASSWORD__";
const DEFAULT_PORT = "5432";
const DEFAULT_DATABASE = "postgres";

function hostComponent(host: string | undefined) {
  const value = (host ?? "").trim();
  if (/^\[[0-9a-fA-F:.]+\]$/.test(value)) return value;
  if (!/^[A-Za-z0-9.-]+$/.test(value)) {
    throw new Error("Invalid Postgres host");
  }
  return value;
}

function portComponent(port: string | undefined) {
  const value = (port || DEFAULT_PORT).trim();
  if (!/^\d+$/.test(value)) throw new Error("Invalid Postgres port");
  const portNumber = Number(value);
  if (portNumber < 1 || portNumber > 65535) {
    throw new Error("Invalid Postgres port");
  }
  return value;
}

function databaseComponent(database: string | undefined) {
  return database?.trim() || DEFAULT_DATABASE;
}

function connectionString({
  host,
  port,
  username,
  database,
  tls,
}: Record<string, string>) {
  const postgresHost = hostComponent(host);
  const postgresPort = portComponent(port);
  const postgresDatabase = databaseComponent(database);
  const encodedUsername = encodeURIComponent(username ?? "");
  const params = new URLSearchParams();
  if (tls === "true") params.set("sslmode", "require");
  const query = params.size > 0 ? `?${params}` : "";
  return `postgresql://${encodedUsername}:${PASSWORD_PLACEHOLDER}@${postgresHost}:${postgresPort}/${encodeURIComponent(postgresDatabase)}${query}`;
}

export const postgres = defineConnector({
  identifier: "postgres",
  name: "Postgres",
  tagLine: "Relational database",
  logo,
  tags: ["data"],
  protocol: "postgres",
  connectionTest: { protocol: "postgres", connectionString },
  defaultUrlPattern: () => "",
  customizeStepHandledInline: true,
  auth: customAuth("postgres"),
  steps: [
    {
      title: "Enter your Postgres connection",
      items: [
        {
          kind: "input",
          label: "Name",
          required: true,
          defaultValue: "Postgres",
          target: { kind: "connectedApp", field: "name" },
        },
        {
          kind: "input",
          label: "Host",
          required: true,
          placeholder: "db.example.com",
          target: { kind: "configuration", field: "host" },
        },
        {
          kind: "input",
          label: "Port",
          required: true,
          defaultValue: "5432",
          target: { kind: "configuration", field: "port" },
        },
        {
          kind: "input",
          label: "Database",
          required: true,
          defaultValue: "postgres",
          target: { kind: "configuration", field: "database" },
        },
        {
          kind: "input",
          label: "Username",
          required: true,
          target: { kind: "configuration", field: "username" },
        },
        {
          kind: "input",
          label: "Password",
          required: true,
          target: { kind: "secrets", field: "password" },
        },
        {
          kind: "select",
          label: "TLS",
          required: true,
          defaultValue: "false",
          target: { kind: "configuration", field: "tls" },
          options: [
            { value: "false", label: "Disabled" },
            { value: "true", label: "Enabled" },
          ],
        },
        {
          kind: "file",
          label: "CA certificate",
          accept: ".pem,.crt,.cer",
          helperText:
            "Upload a PEM CA certificate for private or self-signed Postgres servers.",
          target: { kind: "configuration", field: "tlsCaCertificate" },
          visibleWhen: {
            target: { kind: "configuration", field: "tls" },
            value: "true",
          },
        },
      ],
    },
  ],
  publicEnvVars: (configuration) => ({
    POSTGRES_URL: connectionString(configuration),
  }),
  usageHint: () =>
    "Use process.env.POSTGRES_URL as the Postgres connection string.",
});
